import { Skeleton, Stack, Title } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { getPerformanceCarName } from "./utils";

import { PerformanceChart } from "@/components/elements";
import { useCar } from "@/services/cars/get-car";
import { useMarketCarValuation } from "@/services/market/get-market-car-valuation";
import { tooltipFormatter } from "@/utils/tooltipFormatter";

import type { DatesRange } from "@/types/entity";
import type { PerformanceChart as IPerformanceChart } from "@/types/performance";
import type { FC } from "react";

export type CarCompareValuationProps = {
  car1Id: string;
  car2Id: string;
};

export const CarCompareValuation: FC<CarCompareValuationProps> = ({ car1Id, car2Id }) => {
  const { t } = useTranslation();
  const [range, setRange] = useState<DatesRange>();
  const { data: car1Data, isLoading: car1IsLoading } = useCar({ carId: car1Id });
  const { data: car2Data, isLoading: car2IsLoading } = useCar({ carId: car2Id });

  const { data: car1Valuation, isLoading: car1ValuationIsLoading } = useMarketCarValuation({
    carId: car1Data?.taxonomy_id,
    ...range,
  });
  const { data: car2Valuation, isLoading: car2ValuationIsLoading } = useMarketCarValuation({
    carId: car2Data?.taxonomy_id,
    ...range,
  });

  const isLoading =
    car1IsLoading || car2IsLoading || car1ValuationIsLoading || car2ValuationIsLoading;

  // Choose condition-adjusted series when available (benchmarks contain superb/excellent/good/fair)
  const pickSeries = (valuation?: any, condition?: string) => {
    if (!valuation) return [];
    const benchmarks = valuation.benchmarks || [];
    if (condition) {
      const match = benchmarks.find((b: any) => b.name === condition);
      if (match && match.data) return match.data;
    }
    return valuation.performance?.data || [];
  };

  const series1 = pickSeries(car1Valuation, car1Data?.condition);
  const series2 = pickSeries(car2Valuation, car2Data?.condition);

  const pad = (data: any[], target: any[]) => {
    if (target.length > data.length) {
      return [
        ...target.slice(0, target.length - data.length).map((d) => ({ ...d, value: 0 })),
        ...data,
      ];
    }
    return data;
  };

  const data1 = pad(series1, series2);
  const data2 = pad(series2, series1);

  const chartData1: IPerformanceChart = {
    name: getPerformanceCarName(car1Data),
    data: data1,
  };
  const chartData2: IPerformanceChart = {
    name: getPerformanceCarName(car2Data),
    data: data2,
  };

  return (
    <Stack>
      <Title order={3}>{t("car.valuation")}</Title>
      <Skeleton visible={isLoading}>
        <PerformanceChart
          benchmarks={[chartData2]}
          performance={chartData1}
          onTimeframeChange={setRange}
          defaultBenchmark="__allCars__"
          compareMode
          showPerformanceWhenBenchmarkSelected={false}
          tooltipFormatter={tooltipFormatter}
          restrictKpisByPlan
          showTooltip={false}
          showConditionsTooltip={false}
        />
      </Skeleton>
    </Stack>
  );
};
